import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, App, Button, Segmented, Space } from 'antd';

import { useAccess } from '../../../auth/AccessContext.jsx';
import ActionIcon from '../../../components/ActionIcon.jsx';
import StatusTag from '../../../components/StatusTag.jsx';
import { useLiveTopic } from '../../../live/LiveContext.js';
import { formatInstant } from '../../../format';
import { useStepUp } from '../../../stepup/useStepUp.js';
import LineActionModal from './bankStatement/LineActionModal.jsx';
import { StatementLinesTable } from './bankStatement/StatementLinesTable.jsx';
import StatementStatusPanel from './bankStatement/StatementStatusPanel.jsx';
import ApprovalFundFilter from './ApprovalFundFilter.jsx';
import { STATEMENT_FILTERS, appendPage, countsText, itemsOf, linesQuery, withNote } from './bankStatement/statementLines.js';
import { isIncomplete } from './bankStatement/statementStatus.js';

/** LWM1-479: what the counts' tag says while the statements they rest on are incomplete. */
const INCOMPLETE_HELP = 'Some expected statements are not loaded yet, or are still processing: these counts cover only the statements loaded so far.'
    + ' The status above the lines says which.';

/**
 * The screen's gates (SCREEN_RULES.approval), keyed as statementLines.js
 * ACTIONS: each button's key carries exactly its catalogue action's claims
 * (auth/screenGates.test.js, auth/screenGates.approval-uploads.test.js), and
 * the server checks every one again.
 */
export function useStatementGates() {
    const { canAct } = useAccess();
    const canMatch = canAct('approval', 'match');
    // Approving a hand-made match is the cash checker's step (MATCH_APPROVE, core.cash.approve) - not the
    // order checker's 'approve' (ORDER_APPROVE, core.order.approve): one person may hold either without the other.
    const canApproveMatch = canAct('approval', 'approveMatch');
    const canRejectMatch = canAct('approval', 'rejectMatch');
    const canClear = canAct('approval', 'clear');
    const canReturn = canAct('approval', 'returnLine');
    const canUnreconcile = canAct('approval', 'unreconcile');
    const canManualClear = canAct('approval', 'manualClear');
    const canRefund = canAct('approval', 'refund');
    const canNote = canAct('approval', 'note');
    return {
        match: canMatch, approveMatch: canApproveMatch, rejectMatch: canRejectMatch, clear: canClear, returnLine: canReturn,
        unreconcile: canUnreconcile, manualClear: canManualClear, refund: canRefund, note: canNote
    };
}

/**
 * Transaction Approval > Bank Statement Transactions, on the Console API
 * (IP-58, IP-67..71, IP-111, IP-112; design 2026-10-02 section 8). The lines
 * the accepted statements imported (GET cash/lines), by fund and by view -
 * Outstanding (IP-157: every line someone still has to act on; the screen
 * opens on it), Unreconciled (open credits and claims awaiting approval),
 * Debits (the outstanding debits) and Cleared (the finished lines: matched
 * and cleared, refunded, cleared by hand, or returned) - a page of 100 at a
 * time, Load more following the server's cursor. A finished line is never
 * shown with the outstanding ones.
 *
 * On a line (bankStatement/statementLines.js says which, and why not):
 *   - an open credit is claimed from its candidates (MATCH_CLAIM), and (IP-184)
 *     an unmatched debit from its fund's unpaid redemption payouts;
 *   - a claim is approved or rejected by anyone but its claimant
 *     (MATCH_APPROVE at the match's own amount, MATCH_REJECT);
 *   - a credit in clearing is cleared (LINE_CLEAR) or, matched or
 *     unallocated, returned as a dishonoured cheque (LINE_RETURN);
 *   - cleared money nobody could match is refunded (RECEIPT_REFUND);
 *   - any other left-over line - a debit nothing matched, a credit that is
 *     not an investor's money - is cleared by hand with a reason
 *     (LINE_MANUAL_CLEAR, IP-157);
 * each with a fresh code from the authenticator app (IP-111), and anyone who
 * claims or approves adds notes (LINE_NOTE, no code). After any of them the
 * list is read again from its first page, and the tab's badge with it
 * (onChanged).
 *
 * Above the lines, the statement status panel (LWM1-479, IP-175) says which
 * statements they come from - loaded, processing, failed or missing - for the
 * same fund filter, and is read again whenever the lines are (Refresh, the
 * live topics, after any action). While reconciliation rests on incomplete
 * statements, the counts carry a "Statements incomplete" tag, so they are
 * never read as the full position.
 *
 * The prototype's in-browser reconciliation, its sample statement and its
 * "Clear selected" are gone: the Core matches when a statement is accepted
 * (File Uploads), and a line is never removed - it is explained (a note) or
 * acted on. Gates are SCREEN_RULES.approval's, each carrying its catalogue
 * action's claims (auth/screenGates.test.js, screenGates.approval-uploads.test.js);
 * the server checks every one again.
 *
 * @param {{ onChanged?: () => void }} props
 */
const BankStatementTransactions = ({ onChanged }) => {
    const { api, principal } = useAccess();
    const { message } = App.useApp();
    const { withStepUp, stepUpDialog } = useStepUp();
    const gates = useStatementGates();

    const [filter, setFilter] = useState('OUTSTANDING');
    const [fundId, setFundId] = useState(null);
    const [lines, setLines] = useState([]);
    const [nextCursor, setNextCursor] = useState(null);
    // The answer's counts for the filter shown (design 6); the tab's badge reads its own, over every line.
    const [counts, setCounts] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [refreshedAt, setRefreshedAt] = useState(null);
    const [seenFunds, setSeenFunds] = useState([]);
    const [acting, setActing] = useState(null);
    // LWM1-479: bumped each time the lines are read again, so the status panel reads with them; and whether its answer is incomplete.
    const [statusKey, setStatusKey] = useState(0);
    const [incomplete, setIncomplete] = useState(false);
    // A later read wins: an answer to an earlier filter is dropped.
    const latest = useRef(0);

    const read = useCallback(async ({ cursor = null } = {}) => {
        const ticket = ++latest.current;
        setLoading(true);
        setError(null);
        try {
            const page = await api.cash.lines(linesQuery({ filter, fundId, cursor }));
            if (ticket !== latest.current) return;
            const got = itemsOf(page, 'lines');
            setLines((current) => (cursor ? appendPage(current, got) : got));
            setNextCursor(page?.nextCursor ?? null);
            setCounts(page?.counts ?? null);
            setSeenFunds((current) => [...new Set([...current, ...got.map((l) => l.fundId)])]);
            setRefreshedAt(new Date());
        } catch (e) {
            if (ticket === latest.current) setError(e.message);
        } finally {
            if (ticket === latest.current) setLoading(false);
        }
    }, [api, filter, fundId]);

    const reload = useCallback(() => read(), [read]);

    useEffect(() => {
        reload();
    }, [reload]);

    /** The lines from their first page, and the statement status with them (a new fund the panel follows on its own). */
    const refresh = useCallback(() => {
        setStatusKey((key) => key + 1);
        reload();
    }, [reload]);
    // Live screens (docs/live-sync/implementation-plan.md 4.6): re-read, exactly as Refresh does, when
    // a line was matched (by hand or automatically), approved, rejected, cleared, returned, refunded, noted, unreconciled or cleared by
    // hand, or a statement was loaded.
    useLiveTopic(['cash', 'orders'], refresh);

    /** After a decision: the list, the statement status and the tab's badge are read again. */
    const changed = useCallback(() => {
        refresh();
        onChanged?.();
    }, [refresh, onChanged]);

    const done = (text) => {
        setActing(null);
        message.success(text);
        changed();
    };

    const claimed = () => {
        message.success('Claimed: another person approves the match.');
        changed();
    };

    const noted = (line, note) => setLines((current) => current.map((l) => (l.lineId === line.lineId ? withNote(l, note) : l)));

    return (
        <div className="panel bank-statement">
            <h3 className="panel-title">Bank Statement Transactions</h3>
            <div className="statement-info">
                <ActionIcon name="info" />
                <span>
                    <span>
                        Displays transactions from accepted bank statements. A credit whose reference does not match a transaction can be manually claimed against a subscription by one user and approved by another. Last refreshed {refreshedAt ? formatInstant(refreshedAt) : '—'}.
                    </span>
                    {countsText(counts) && (
                        <span className="statement-counts">
                            {countsText(counts)}.
                            {incomplete && <StatusTag color="warning" label="Statements incomplete" help={INCOMPLETE_HELP} className="statement-incomplete-tag" />}
                        </span>
                    )}
                </span>
            </div>
            <div className="statement-toolbar">
                <ApprovalFundFilter fundId={fundId} onChange={setFundId} seen={seenFunds} />
                <Space wrap>
                    <Button className="refresh-button" icon={<ActionIcon name="refresh" />} onClick={changed} loading={loading}>Refresh</Button>
                    <Segmented
                        className="statement-filter-segmented"
                        value={filter}
                        onChange={setFilter}
                        options={STATEMENT_FILTERS.map(({ value, label }) => ({ value, label }))}
                    />
                </Space>
            </div>
            <StatementStatusPanel fundId={fundId} refreshKey={statusKey} onStatus={(status) => setIncomplete(isIncomplete(status))} />
            {error && <Alert type="error" showIcon title={error} className="auth-alert" />}
            <StatementLinesTable
                lines={lines}
                loading={loading}
                me={principal?.uid ?? null}
                gates={gates}
                api={api}
                withStepUp={withStepUp}
                onAction={(key, line) => setActing({ key, line })}
                onClaimed={claimed}
                onStale={changed}
                onNoted={noted}
            />
            {nextCursor && (
                <div className="statement-more">
                    <Button onClick={() => read({ cursor: nextCursor })} loading={loading}>Load more</Button>
                </div>
            )}
            <LineActionModal
                action={acting?.key ?? null}
                line={acting?.line ?? null}
                api={api}
                withStepUp={withStepUp}
                onClose={() => setActing(null)}
                onDone={done}
                onStale={changed}
            />
            {stepUpDialog}
        </div>
    );
};

export default BankStatementTransactions;
